import React, { useEffect, useMemo, useRef, useState } from 'react'
import Icon, {icons} from './Icons'

import '../css/SelectDate.scss'

const localeByLanguage = {
    es: 'es-ES',
    en: 'en-GB',
    de: 'de-DE',
    fr: 'fr-FR',
    it: 'it-IT',
    ru: 'ru-RU',
    cs: 'cs-CZ',
    nb: 'nb-NO',
    da: 'da-DK',
    sv: 'sv-SE'
}

const navigationLabels = {
    es: { previous: 'Mes anterior', next: 'Mes siguiente' },
    en: { previous: 'Previous month', next: 'Next month' },
    de: { previous: 'Vorheriger Monat', next: 'Nächster Monat' },
    fr: { previous: 'Mois précédent', next: 'Mois suivant' },
    it: { previous: 'Mese precedente', next: 'Mese successivo' },
    ru: { previous: 'Предыдущий месяц', next: 'Следующий месяц' },
    cs: { previous: 'Předchozí měsíc', next: 'Další měsíc' },
    nb: { previous: 'Forrige måned', next: 'Neste måned' },
    da: { previous: 'Forrige måned', next: 'Næste måned' },
    sv: { previous: 'Föregående månad', next: 'Nästa månad' }
}

const getLocale = language => localeByLanguage[(language || '').toLowerCase()] || language || 'es-ES'

const getNavigationLabels = language => navigationLabels[(language || '').toLowerCase()] || navigationLabels.es

const toDate = date => {
    const result = new Date(
        parseInt(date.year, 10),
        parseInt(date.month, 10) - 1,
        parseInt(date.day, 10)
    )
    result.setHours(0, 0, 0, 0)
    return result
}

const toValue = date => ({
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear()
})

const getToday = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
}

const addMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() + months, 1)

const isBefore = (date, minDate) => date.getTime() < minDate.getTime()

const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

const isBeforeMonth = (a, b) =>
    a.getFullYear() < b.getFullYear() ||
    (a.getFullYear() === b.getFullYear() && a.getMonth() < b.getMonth())

const formatWeekday = (date, formatter) =>
    formatter.format(date).replace('.', '')

const formatDisplayDate = (date, formatter) => {
    const weekday = formatWeekday(date, formatter)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${weekday} ${day}/${month}`
}

const formatFullDate = (date, formatter) => formatter.format(date)

const getCalendarDays = displayMonth => {
    const firstDay = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), 1)
    const mondayOffset = (firstDay.getDay() + 6) % 7
    const startDate = new Date(firstDay)
    startDate.setDate(firstDay.getDate() - mondayOffset)

    return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(startDate)
        date.setDate(startDate.getDate() + index)
        return date
    })
}

export default props => {
    const {
        date,
        style,
        onChange,
        setActiveInput,
        inputType,
        language,
        ariaLabel,
        forceClose
    } = props

    const locale = getLocale(language)
    const navLabels = getNavigationLabels(language)
    const minDate = useMemo(getToday, [])
    const weekdayFormatter = useMemo(
        () => new Intl.DateTimeFormat(locale, { weekday: 'short' }),
        [locale]
    )
    const fullDateFormatter = useMemo(
        () => new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
        [locale]
    )
    const monthFormatter = useMemo(
        () => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
        [locale]
    )
    const dateDay = date.day
    const dateMonth = date.month
    const dateYear = date.year
    const selectedDate = useMemo(
        () => toDate({ day: dateDay, month: dateMonth, year: dateYear }),
        [dateDay, dateMonth, dateYear]
    )
    const [isOpen, setIsOpen] = useState(false)
    const [displayMonth, setDisplayMonth] = useState(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1))
    const containerRef = useRef(null)

    useEffect(() => {
        if (isOpen && forceClose) setIsOpen(false)
    }, [forceClose, isOpen])

    useEffect(() => {
        if (!isOpen) return

        const closeOnOutsideClick = event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false)
                setActiveInput(null)
            }
        }

        document.addEventListener('mousedown', closeOnOutsideClick)
        document.addEventListener('touchstart', closeOnOutsideClick)

        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick)
            document.removeEventListener('touchstart', closeOnOutsideClick)
        }
    }, [isOpen, setActiveInput])

    const openPicker = () => {
        const nextOpen = !isOpen
        setIsOpen(nextOpen)
        setDisplayMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1))
        setActiveInput(nextOpen ? inputType : null)
    }

    const closePicker = () => {
        setIsOpen(false)
        setActiveInput(null)
    }

    const selectDate = selected => {
        if (isBefore(selected, minDate)) return
        onChange(toValue(selected))
        closePicker()
    }

    const monthLabel = monthFormatter.format(displayMonth)
    const days = useMemo(() => getCalendarDays(displayMonth), [displayMonth])
    const weekDays = useMemo(() => getCalendarDays(new Date(2026, 0, 5)).slice(0, 7), [])
    const previousMonth = addMonths(displayMonth, -1)
    const canGoPrevious = !isBeforeMonth(previousMonth, new Date(minDate.getFullYear(), minDate.getMonth(), 1))

    return (
        <div className="date-container" style={style} ref={containerRef}>
            <button
                type="button"
                className="date-label"
                aria-label={ariaLabel}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                onClick={openPicker}
            >
                <Icon icon={icons.date} className="date-icon" />
                <span className="date-value">{formatDisplayDate(selectedDate, weekdayFormatter)}</span>
            </button>

            {isOpen && <div className="date-picker-backdrop" onClick={closePicker} />}

            {isOpen && (
                <div className="date-picker-panel" role="dialog" aria-label={ariaLabel}>
                    <div className="date-picker-header">
                        <button
                            type="button"
                            className="date-picker-nav"
                            aria-label={navLabels.previous}
                            disabled={!canGoPrevious}
                            onClick={() => canGoPrevious && setDisplayMonth(previousMonth)}
                        >
                            {'<'}
                        </button>
                        <div className="date-picker-month">{monthLabel}</div>
                        <button
                            type="button"
                            className="date-picker-nav"
                            aria-label={navLabels.next}
                            onClick={() => setDisplayMonth(addMonths(displayMonth, 1))}
                        >
                            {'>'}
                        </button>
                    </div>

                    <div className="date-picker-weekdays">
                        {weekDays.map(day => (
                            <div className="date-picker-weekday" key={day.toISOString()}>
                                {formatWeekday(day, weekdayFormatter)}
                            </div>
                        ))}
                    </div>

                    <div className="date-picker-days">
                        {days.map(day => {
                            const disabled = isBefore(day, minDate)
                            const outside = day.getMonth() !== displayMonth.getMonth()
                            const selected = isSameDay(day, selectedDate)

                            return (
                                <button
                                    type="button"
                                    key={day.toISOString()}
                                    className={[
                                        'date-picker-day',
                                        outside ? 'is-outside' : '',
                                        selected ? 'is-selected' : '',
                                        disabled ? 'is-disabled' : ''
                                    ].filter(Boolean).join(' ')}
                                    disabled={disabled}
                                    aria-pressed={selected}
                                    aria-label={formatFullDate(day, fullDateFormatter)}
                                    onClick={() => selectDate(day)}
                                >
                                    {day.getDate()}
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}
